/** Task detail as a right-side overlay, so the table keeps its full width.
 *  The history timeline is the artifact the whole data model exists to serve. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type Task, type TaskEvent, type ThreadMessage } from '@/api/client'
import { EditTaskForm } from './EditTaskForm'
import { useAuth } from '@/lib/auth'
import { AttachmentPanel } from './Attachments'
import { DocumentChecklist } from './DocumentChecklist'
import { approvalProgress, awaitingMyDecision }
  from '@/components/PeoplePicker'
import { fmtStamp, fmtTime, ErrorNote, Spinner, StatusChip, Tag, fmtDate } from './ui'
import { auditOnly, eventLabel } from './TaskDrawer.thread'

// Where an event came from. TELEGRAM had to be here or a tap on Telegram would be
// shown as having happened on WhatsApp.
// `bg-done-tint`/`bg-primary-tint`, not the literal #DDF3E4 and #DCEEFB they used to
// be. Those two values are the DEFAULT theme's pale tints, so under either dark theme
// these were light slabs - and the ink on them IS theme-driven, which left "WhatsApp"
// as near-white text on a near-white chip. Same bug class as the `.th` and rail
// literals; `theme.contrast.test.ts` cannot see these because they are inline here
// rather than in `index.css`.
const CHANNEL: Record<string, string> = {
  WEB: 'bg-rule text-ink-2',
  WHATSAPP: 'bg-done-tint text-done-ink',
  TELEGRAM: 'bg-primary-tint text-primary',
  SYSTEM: 'bg-open-tint text-open-ink',
}
const DOT: Record<string, string> = {
  COMPLETED: 'border-done', COMPLETION_SUBMITTED: 'border-done', APPROVED: 'border-done',
  REJECTED: 'border-rej', REWORK_STARTED: 'border-rej', CANCELLED: 'border-rej',
  APPROVAL_REQUESTED: 'border-pend',
}

export function TaskDrawer({ task, onClose }: { task: Task; onClose: () => void }) {
  const { me, can } = useAuth()
  const qc = useQueryClient()
  const [remark, setRemark] = useState('')
  const [editing, setEditing] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  const { data: history, isLoading } = useQuery({
    queryKey: ['history', task.task_id],
    queryFn: () => api.get<TaskEvent[]>(`/tasks/${task.task_id}/history`),
  })
  const { data: messages } = useQuery({
    queryKey: ['thread', task.task_id],
    queryFn: () => api.get<ThreadMessage[]>(`/tasks/${task.task_id}/thread`),
  })
  // Conversation first. It is what somebody opening a task wants to read; the trail
  // is what they come back for when they need to know exactly what happened.
  const [tab, setTab] = useState<'talk' | 'trail'>('talk')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['history', task.task_id] })
    qc.invalidateQueries({ queryKey: ['thread', task.task_id] })
    qc.invalidateQueries({ queryKey: ['day'] })
    qc.invalidateQueries({ queryKey: ['queue-count'] })
  }

  const act = useMutation({
    mutationFn: (m: { path: string; body?: unknown }) =>
      api.post(`/tasks/${task.task_id}${m.path}`, m.body),
    onSuccess: () => { setErr(null); setRemark(''); invalidate() },
    onError: setErr,
  })

  // Every doer, not the `responsible_user_id` mirror - that column holds the first of
  // them and nothing more. It also gates the attach buttons below, where reading the
  // mirror cost everybody but one of a shared task's doers a capability the server had
  // always granted them: `may_attach` asks `is_responsible`, which reads the people.
  // The same test the server makes, so the button and the answer cannot disagree.
  const mine = (task.people ?? []).some(
    (p) => p.kind === 'RESPONSIBLE' && p.user_id === me?.user_id)
  // Membership, and whose turn it is. Being one of the approvers is not the same as
  // having a decision left to give - showing the buttons to somebody who has already
  // approved invites them to do it twice.
  const myTurn = awaitingMyDecision(task, me?.user_id)
  /** Whether changing THIS task would actually be allowed - edit, cancel, reopen.
   *
   *  `can('edit')` and friends are the task-less capability answer, "would this person
   *  ever be allowed to?". For somebody who files their own work the real rule is
   *  narrower: they may change what they raised, not what the firm assigned them. All
   *  three buttons need it, not just Edit - Cancel was the one caught on the screen,
   *  sitting under a task the Owner had given him and answering 403 on click.
   *
   *  Owners and managers are unaffected; a manager's reach is decided server-side by
   *  their subtree, which the browser has no business recomputing. */
  const mayChangeThis = !!me && (me.is_owner || me.is_manager
                                 || task.created_by === me.user_id)
  const terminal = task.status === 'COMPLETED' || task.status === 'CANCELLED'

  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[520px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule">
          <div className="flex items-center gap-2.5 mb-1.5">
            <span className="mono text-[13px] text-primary font-medium">#{task.ref}</span>
            <StatusChip status={task.status} approvalStatus={task.approval_status} />
            {task.carry_forward_count > 0 && <Tag>carried {task.carry_forward_count}×</Tag>}
            {task.is_recurring && <Tag>⟳ recurring</Tag>}
            <span className="flex-1" />
            {can('edit') && mayChangeThis && !editing && (
              <button className="btn btn-sm" disabled={terminal}
                      title={terminal
                        ? `A ${task.status.toLowerCase()} task is a record now — only a `
                          + 'remark can be added'
                        : undefined}
                      onClick={() => { setErr(null); setEditing(true) }}>
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg leading-none px-1"
                    aria-label="Close">×</button>
          </div>
          <h3 className="text-base font-semibold leading-snug tracking-tight">{task.task}</h3>
          <p className="text-[12.5px] text-ink-3 mt-1">
            {task.company} · {task.priority} · {fmtDate(task.date)}
          </p>
        </div>

        {editing && (
          <EditTaskForm task={task} onDone={() => setEditing(false)}
                        onCancel={() => setEditing(false)} onSaved={invalidate} />
        )}

        <div className="flex flex-wrap gap-1.5 px-5 py-2.5 border-b border-rule">
          {[
            ['Responsible', task.responsible_person || '—'],
            // The progress rides on the approver chip, which is where somebody
            // looking for "has this been approved yet" already looks.
            ['Approver', [task.approver || 'none', approvalProgress(task)]
              .filter(Boolean).join(' · ')],
          ].map(([k, v]) => (
            <span key={k} className="text-[11.8px] text-ink-2 bg-paper border border-rule rounded px-2 py-0.5">
              {k} <b className="font-semibold text-ink">{v}</b>
            </span>
          ))}
          <span className="mono text-[11px] text-ink-2 bg-paper border border-rule rounded px-2 py-0.5">
            {/* `original_date` is the day the task was FOR when it was written, not
                when it was written - `TaskService` sets it to the task's own day, and
                `created_at` is a separate field shown in the history as CREATED. It
                earns its place on a carried task ("originally for the 21st, now on
                the 29th"); "raised" just described it wrongly. */}
            originally for {fmtDate(task.original_date)}
          </span>
          <span className="mono text-[11px] text-ink-2 bg-paper border border-rule rounded px-2 py-0.5">
            rowversion {task.row_version}
          </span>
        </div>

        {/* Above the files themselves, because what was *asked for* is the frame the
            approver reads the files in - "two of the three arrived" is the useful
            sentence, and it cannot be told by a list of filenames. */}
        <DocumentChecklist
          task={task}
          canAttach={!!me && (me.is_owner || mine)}
          canEdit={!!me?.is_owner} />

        {/* Between the metadata and the history: an attachment belongs to the task,
            not to one event in it. The panel returns null when the viewer is not
            allowed to see attachments at all. */}
        <AttachmentPanel
          task={task}
          canAttach={!!me && (me.is_owner || mine)} />

        <div className="flex-1 overflow-auto">
          {/* Two halves of the same record, and deliberately not one list. The trail
              answers "what happened to this task"; the conversation answers "what are
              we saying to each other". Rendered together, a remark showed up as
              `REMARK_ADDED` above a quote block - an audit row pretending to be a
              message. So the message-bearing events are filtered OUT of the trail and
              rendered here instead, once. See `TaskDrawer.thread.ts`. */}
          <div className="flex items-center gap-1.5 px-5 pt-3 pb-2">
            {([['talk', `Conversation${messages?.length ? ` · ${messages.length}` : ''}`],
               ['trail', `History · ${auditOnly(history ?? []).length}`]] as const)
              .map(([key, label]) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                        aria-pressed={tab === key}
                        className={`px-2.5 h-7 rounded-md text-[12px] font-medium
                                    transition-colors ${
                          tab === key ? 'bg-primary-tint text-primary'
                                      : 'text-ink-3 hover:text-ink'}`}>
                  {label}
                </button>
              ))}
            <span className="flex-1" />
            {tab === 'trail' && (
              <span className="text-2xs uppercase tracking-wider text-ink-3">
                append-only
              </span>
            )}
          </div>

          {tab === 'talk' && (
            <div className="px-5 pb-4">
              {!messages ? <Spinner /> : messages.length === 0 ? (
                <p className="text-[12.5px] text-ink-3 py-2">
                  Nothing said on this task yet.
                </p>
              ) : messages.map((m) => (
                <div key={m.message_id} className="mb-2.5 last:mb-0">
                  <div className="flex items-baseline gap-2">
                    <b className="text-[12.8px] font-semibold">{m.author_name}</b>
                    <span className={`mono text-[9.5px] font-semibold rounded px-1 py-px
                      ${CHANNEL[m.channel] ?? CHANNEL.SYSTEM}`}>{m.channel}</span>
                    <span className="mono text-[11px] text-ink-3 ml-auto whitespace-nowrap">
                      {fmtTime(m.created_at)}
                    </span>
                  </div>
                  <div className="mt-1 rounded-md bg-paper border border-rule px-2.5 py-1.5
                                  text-[12.8px] leading-snug text-ink whitespace-pre-wrap">
                    {m.body}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'trail' && (isLoading ? <Spinner /> : (
            <div className="px-5 pb-4">
              {auditOnly(history ?? []).map((e, i, shown) => (
                <div key={e.event_id}
                     className={`relative pl-6 pb-2.5 ml-1.5 border-l-[1.5px]
                       ${i === (shown.length - 1) ? 'border-transparent' : 'border-rule'}`}>
                  <span className={`absolute -left-[6.5px] top-0.5 h-[11px] w-[11px] rounded-full
                    bg-surface border-[2.5px] ${DOT[e.event_type] ?? 'border-open'}`} />
                  <div className="flex items-baseline gap-2">
                    {/* In English. These were rendered as raw enum names -
                        `COMPLETION_SUBMITTED` in bold - on the one panel
                        non-technical staff read most closely. */}
                    <b className="text-[12.8px] font-semibold tracking-wide">
                      {eventLabel(e.event_type)}
                    </b>
                    <span className={`mono text-[9.5px] font-semibold rounded px-1 py-px
                      ${CHANNEL[e.channel] ?? CHANNEL.SYSTEM}`}>{e.channel}</span>
                    <span className="mono text-[11px] text-ink-3 ml-auto whitespace-nowrap">
                      {/* The audit trail, in the firm's zone. Sliced out of the
                          stamp it showed UTC - every event five and a half hours
                          before it happened, on the one panel whose whole job is
                          saying when things happened. */}
                      {fmtStamp(e.timestamp)}
                    </span>
                  </div>
                  <div className="text-[11.8px] text-ink-3 mt-0.5">
                    {e.actor_user_id}{e.actor_role ? ` · ${e.actor_role}` : ''}
                  </div>
                  {e.reason && (
                    <div className={`mt-1.5 rounded-r px-2.5 py-1.5 text-[12.2px] leading-snug border-l-[2.5px]
                      ${e.event_type === 'REJECTED'
                        ? 'bg-rej-tint border-rej text-rej-ink'
                        : 'bg-paper border-rule text-ink-2'}`}>
                      {e.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="border-t border-rule p-4 space-y-2.5">
          {err ? <ErrorNote error={err} /> : null}
          {/* A remark is allowed on a finished task too - it appends to the record
              rather than rewriting it, and the history keeps every one. Hiding it
              here while WhatsApp allowed it would be two different rules for the
              same action. Completing, though, only applies to open work. */}
          {(mine || can('create')) && (
            <>
              <textarea value={remark} onChange={(e) => setRemark(e.target.value)}
                        placeholder={terminal
                          ? 'Add a comment — the task stays ' + task.status.toLowerCase()
                          : 'Add a comment — this does not send it for approval'}
                        rows={2} maxLength={1000}
                        className="w-full rounded-md border border-rule bg-surface text-ink
                                   px-2.5 py-2 text-[13px] placeholder:text-ink-3
                                   focus:border-primary" />
              <div className="flex gap-2">
                {/* `/thread`, not `/remark`. The two are the same write underneath -
                    the older route was reimplemented over this one so there is a
                    single writer - but this one hands back the conversation with the
                    task, so the list below refreshes from the same round trip
                    instead of a second fetch. */}
                <button className="btn"
                        disabled={!remark.trim() || act.isPending}
                        onClick={() => {
                          setTab('talk')
                          act.mutate({ path: '/thread', body: { text: remark } })
                        }}>
                  Comment
                </button>
                {mine && !terminal && task.status !== 'PENDING_APPROVAL' && (
                  <button className="btn btn-good"
                          disabled={act.isPending}
                          onClick={() => act.mutate({ path: '/complete', body: { remark } })}>
                    Send for approval
                  </button>
                )}
                {myTurn && task.status === 'PENDING_APPROVAL' && (
                  <ApproveButtons taskId={task.task_id} onDone={invalidate} onError={setErr} />
                )}
              </div>
            </>
          )}
          {terminal && can('reopen') && mayChangeThis && (
            <ReopenButton taskId={task.task_id} onDone={invalidate} onError={setErr} />
          )}
          {!terminal && can('cancel') && mayChangeThis && (
            <CancelButton taskId={task.task_id} ref_={task.ref}
                          onDone={invalidate} onError={setErr} />
          )}
        </div>
      </aside>
    </>
  )
}

/** Withdrawing work that should not have been raised.
 *
 *  Not a delete: the task becomes CANCELLED and its whole history survives, which is
 *  why the reason is mandatory — `ReasonIn` enforces ten characters, and it is the
 *  only record of why the work stopped.
 */
function CancelButton({ taskId, ref_, onDone, onError }: {
  taskId: string; ref_: number; onDone: () => void; onError: (e: unknown) => void
}) {
  const [reason, setReason] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: () => api.post(`/tasks/${taskId}/cancel`, { reason }),
    onSuccess: () => { setReason(null); onDone() },
    onError,
  })

  if (reason === null) {
    return (
      <button className="btn" onClick={() => setReason('')}>
        Cancel this task
      </button>
    )
  }
  const short = reason.trim().length < 10
  return (
    <div className="space-y-2">
      <p className="text-[12px] text-ink-2">
        Cancelling #{ref_} stops the work. The task and everything that happened to it
        stay on the record — this is not a delete.
      </p>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
                rows={2} maxLength={1000}
                placeholder="Why is this being cancelled? It goes into the permanent history."
                className="w-full rounded-md border border-rule px-2.5 py-2 text-[13px]" />
      <div className="flex gap-2 items-center">
        <button className="btn btn-bad" disabled={short || m.isPending}
                onClick={() => m.mutate()}>
          {m.isPending ? 'Cancelling…' : 'Cancel the task'}
        </button>
        <button className="btn" onClick={() => setReason(null)}>Keep it</button>
        {short && (
          <span className="text-[11.5px] text-ink-3">
            {10 - reason.trim().length} more character(s)
          </span>
        )}
      </div>
    </div>
  )
}

function ApproveButtons({ taskId, onDone, onError }: {
  taskId: string; onDone: () => void; onError: (e: unknown) => void
}) {
  const [reason, setReason] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: (b: { approve: boolean; reason?: string }) =>
      b.approve ? api.post(`/approvals/${taskId}/approve`)
                : api.post(`/approvals/${taskId}/reject`, { reason: b.reason }),
    onSuccess: () => { setReason(null); onDone() },
    onError,
  })
  if (reason === null) {
    return (
      <>
        <button className="btn btn-good" disabled={m.isPending}
                onClick={() => m.mutate({ approve: true })}>Approve</button>
        <button className="btn btn-bad" onClick={() => setReason('')}>Reject…</button>
      </>
    )
  }
  return (
    <div className="w-full space-y-2">
      <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
                rows={2} placeholder="Why is this being rejected? It is sent to them word for word."
                className="w-full rounded-md border border-rej/40 px-2.5 py-2 text-[13px]" />
      <div className="flex gap-2 items-center">
        <button className="btn btn-bad" disabled={reason.trim().length < 10 || m.isPending}
                onClick={() => m.mutate({ approve: false, reason })}>
          Send rejection
        </button>
        <button className="btn" onClick={() => setReason(null)}>Cancel</button>
        <span className="text-[11.5px] text-ink-3">
          {reason.trim().length < 10 ? `${10 - reason.trim().length} more characters` : ''}
        </span>
      </div>
    </div>
  )
}

function ReopenButton({ taskId, onDone, onError }: {
  taskId: string; onDone: () => void; onError: (e: unknown) => void
}) {
  const [reason, setReason] = useState<string | null>(null)
  const m = useMutation({
    mutationFn: (r: string) => api.post(`/tasks/${taskId}/reopen`, { reason: r }),
    onSuccess: () => { setReason(null); onDone() },
    onError,
  })
  if (reason === null) return <button className="btn" onClick={() => setReason('')}>Reopen…</button>
  return (
    <div className="space-y-2">
      <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
             placeholder="Why reopen? Kept in the permanent history."
             className="field" />
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={reason.trim().length < 10 || m.isPending}
                onClick={() => m.mutate(reason)}>Reopen task</button>
        <button className="btn" onClick={() => setReason(null)}>Cancel</button>
      </div>
    </div>
  )
}
